import { translate } from "@atoms/atom-client/i18n";
import { Sides } from "@project/emerge-types";

/** Mounting sides selectable for exterior rollers (not Bodenrollen). */
export const ROLLER_MOUNT_SIDE_OPTIONS: ReadonlyArray<{
  value: Sides;
  readonly label: string;
}> = [
  {
    value: Sides.LEFT,
    get label() {
      return translate("app.shared.rollerSides.left", "Links");
    },
  },
  {
    value: Sides.RIGHT,
    get label() {
      return translate("app.shared.rollerSides.right", "Rechts");
    },
  },
  {
    value: Sides.FRONT,
    get label() {
      return translate("app.shared.rollerSides.front", "Vorne");
    },
  },
  {
    value: Sides.BACK,
    get label() {
      return translate("app.shared.rollerSides.back", "Hinten");
    },
  },
];

const ROLLER_TYPE = {
  NONE: 0,
  CORNER_INBOARD: 1,
  CORNER_OUTBOARD: 2,
  EDGE: 3,
  CREEPER: 4,
} as const;

export function rollerMountSideRequired(rollerType: unknown): boolean {
  return (
    rollerType === ROLLER_TYPE.CORNER_INBOARD ||
    rollerType === ROLLER_TYPE.CORNER_OUTBOARD ||
    rollerType === ROLLER_TYPE.EDGE
  );
}

/**
 * Stable import/export identity for mount-side rollers (same base name, different side).
 */
export function rollerUserOptionImportKey(
  data: Record<string, unknown>,
): string | null {
  if (data.kind !== "RollerType") return null;
  if (!rollerMountSideRequired(data.rollerType)) return null;
  if (typeof data.side !== "number") return null;
  const name = typeof data.name === "string" ? data.name.trim() : "";
  if (!name) return null;
  return `${name}|${data.side}`;
}

/**
 * Normalizes RollerType rows on JSON import/export:
 * - preserves numeric `side` for mount-side roller types
 * - omits `side` for Bodenrollen (CREEPER) and NONE
 */
export function normalizeRollerUserOptionForImport(
  data: Record<string, unknown>,
): Record<string, unknown> {
  if (data.kind !== "RollerType") return data;

  const rollerType = data.rollerType;
  const normalized = { ...data };

  if (!rollerMountSideRequired(rollerType)) {
    delete normalized.side;
    return normalized;
  }

  if (typeof normalized.side !== "number") {
    delete normalized.side;
  }

  return normalized;
}
